import React, { useEffect, useMemo, useState } from 'react';
import { Modal, DatePicker, Descriptions, Tooltip, message } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import {
    SUBMIT_REALTIME_INVOICE_APPLICATION,
    SubmitRealtimeInvoiceApplicationResponse,
} from '../graphql/mutations/realtimeInvoiceMutations';
import { RealtimeDailyAggregateType } from '../graphql/types/RealtimeInvoiceType';
import { formatCentsAmount, formatYyyymmdd } from '../../../../../utils/invoiceAmount';

interface SubmitInvoiceApplicationModalProps {
    open: boolean;
    settlementClientId: string;
    /** 已勾选、remaining>0 的日聚合凭证 */
    selectedAggregates: RealtimeDailyAggregateType[];
    currency?: string | null;
    /** 客户名（来源 channel，不可改，仅展示） */
    customerName?: string | null;
    /** 渠道 ID（来源 channel，不可改，仅展示，附在客户名后） */
    channelId?: string | null;
    /** 模板主体名（contracting_party_config.title，不可改，仅展示） */
    corporateEntityTitle?: string | null;
    /** 账期天数（channel_settlement.net_day），默认 Due Date = 今日 + netDay */
    netDay?: number | null;
    onCancel: () => void;
    onSuccess: () => void;
}

/**
 * 实时 Invoice 开具申请弹窗：确认客户/主体/凭证摘要 + Due Date，提交开票。
 * Due Date 默认「今日 + netDay」、可改、必须 ≥ 今日。
 */
const SubmitInvoiceApplicationModal: React.FC<SubmitInvoiceApplicationModalProps> = ({
    open,
    settlementClientId,
    selectedAggregates,
    currency,
    customerName,
    channelId,
    corporateEntityTitle,
    netDay,
    onCancel,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [dueDate, setDueDate] = useState<Dayjs>(dayjs().add(netDay ?? 0, 'day'));

    useEffect(() => {
        if (open) {
            setDueDate(dayjs().add(netDay ?? 0, 'day'));
        }
    }, [open, netDay]);

    const [submit, { loading }] = useMutation<SubmitRealtimeInvoiceApplicationResponse>(
        SUBMIT_REALTIME_INVOICE_APPLICATION,
    );

    const summary = useMemo(() => {
        // GraphQL Long 标量在前端是字符串，必须 Number() 强转后再求和，否则 `+` 会做字符串拼接
        const totalOrders = selectedAggregates.reduce((sum, a) => sum + Number(a.orderCount ?? 0), 0);
        const totalAmount = selectedAggregates.reduce((sum, a) => sum + Number(a.remainingAmount ?? 0), 0);
        const dateText = selectedAggregates
            .map((a) => a.businessDate)
            .filter((d): d is number => d != null)
            .sort((x, y) => x - y)
            .map((d) => formatYyyymmdd(d))
            .join(', ');
        return { totalOrders, totalAmount, dateText: dateText || '-' };
    }, [selectedAggregates]);

    const handleOk = async () => {
        if (selectedAggregates.length === 0) {
            message.warning(t('settlement.realtimeInvoice.submitModal.noAggregate'));
            return;
        }
        if (dueDate.isBefore(dayjs(), 'day')) {
            message.warning(t('settlement.realtimeInvoice.submitModal.dueDateInvalid'));
            return;
        }
        try {
            const { data } = await submit({
                variables: {
                    settlementClientId,
                    aggregateIds: selectedAggregates.map((a) => a.aggregateId),
                    dueDate: Number(dueDate.format('YYYYMMDD')),
                },
            });
            const result = data?.submitRealtimeInvoiceApplication;
            if (result?.success) {
                message.success(t('settlement.realtimeInvoice.submitModal.success'));
                onSuccess();
            } else {
                message.error(result?.message || t('settlement.realtimeInvoice.submitModal.failed'));
            }
        } catch (e) {
            message.error((e as Error).message || t('settlement.realtimeInvoice.submitModal.failed'));
        }
    };

    return (
        <Modal
            title={t('settlement.realtimeInvoice.submitModal.title')}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={loading}
            okText={t('settlement.realtimeInvoice.actions.submit')}
            destroyOnClose
        >
            <Descriptions column={1} bordered size="small">
                <Descriptions.Item label={t('settlement.realtimeInvoice.submitModal.customer')}>
                    {customerName || '-'}
                    {channelId ? ` (${channelId})` : ''}
                </Descriptions.Item>
                <Descriptions.Item label={t('settlement.realtimeInvoice.submitModal.dateRange')}>
                    <Tooltip title={summary.dateText}>
                        <div className="truncate" style={{ maxWidth: 360 }}>
                            {summary.dateText}
                        </div>
                    </Tooltip>
                </Descriptions.Item>
                <Descriptions.Item label={t('settlement.realtimeInvoice.columns.totalOrders')}>
                    {summary.totalOrders}
                </Descriptions.Item>
                <Descriptions.Item label={t('settlement.realtimeInvoice.columns.totalAmount')}>
                    {formatCentsAmount(summary.totalAmount, currency)}
                </Descriptions.Item>
                <Descriptions.Item label={t('settlement.realtimeInvoice.submitModal.dueDate')}>
                    <div className="flex items-center gap-2">
                        <DatePicker
                            value={dueDate}
                            onChange={(d) => d && setDueDate(d)}
                            allowClear={false}
                            disabledDate={(d) => d && d.isBefore(dayjs(), 'day')}
                        />
                        {netDay != null && (
                            <span className="text-xs text-gray-400">
                                {t('settlement.realtimeInvoice.submitModal.netDayHint', { netDay })}
                            </span>
                        )}
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label={t('settlement.realtimeInvoice.submitModal.templateSubject')}>
                    {corporateEntityTitle || '-'}
                </Descriptions.Item>
            </Descriptions>
        </Modal>
    );
};

export default SubmitInvoiceApplicationModal;
